import { MapPin, Target, Award, Users } from "lucide-react";

const TIMELINE = [
  { year: "2015", event: "Founded", desc: "Established Qaaf Group with a vision to bring Pakistani rice excellence to global markets." },
  { year: "2017", event: "ISO Certification", desc: "Achieved ISO 9001:2015 and ISO 22000 certifications for quality management." },
  { year: "2019", event: "Global Expansion", desc: "Expanded to 15+ countries across Asia, Europe, Middle East and Americas." },
  { year: "2023", event: "Capacity Growth", desc: "Increased processing capacity to 50,000 MT/month with new facility." },
  { year: "2026", event: "Sustainable Future", desc: "Launched organic certification program with partner farms." },
];

const FACTS = [
  { label: "Established", value: "2015" },
  { label: "Partner Farms", value: "250+" },
  { label: "Countries Served", value: "15+" },
  { label: "Monthly Capacity", value: "50,000 MT" },
];

export default function About() {
  return (
    <div data-testid="page-about" className="py-16 md:py-20">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="overline">About Qaaf Group</div>
        <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight max-w-4xl">
          From Punjab's fertile plains to tables worldwide.
        </h1>
        <p className="mt-8 text-foreground/75 text-lg max-w-3xl leading-relaxed">
          Qaaf Group was founded on a simple principle: Pakistan's rice heritage deserves 
          world-class export standards. We bridge the gap between Punjab's traditional farming 
          heritage and modern international supply chains.
        </p>

        {/* Origin Story */}
        <div className="mt-24 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline">Origin · Punjab · Pakistan</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight">
              Home of the world's finest Basmati
            </h2>
            <p className="mt-6 text-foreground/75 leading-relaxed">
              Punjab's fertile alluvial plains, fed by the mighty Indus river system, have sustained 
              civilizations for millennia. Today, this region produces the world's most 
              aromatic and sought-after Basmati rice.
            </p>
            <p className="mt-4 text-foreground/75 leading-relaxed">
              We work directly with farmer cooperatives across Punjab's prime rice-growing zones — 
              ensuring fair prices for farmers and consistent quality for our global customers.
            </p>
          </div>
          <div className="aspect-[4/3] bg-[#141212] overflow-hidden">
            <img 
              src="https://images.pexels.com/photos/2526935/pexels-photo-2526935.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&fit=crop" 
              alt="Punjab farmlands" 
              className="w-full h-full object-cover" 
            />
          </div>
        </div>

        {/* Values */}
        <div className="mt-28">
          <div className="overline">Our Standards</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight">
            Excellence at every stage
          </h2>
          
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {[
              { Icon: MapPin, t: "Traceability", d: "Full farm-to-port tracking. Every batch is traceable to its source field." },
              { Icon: Target, t: "Quality First", d: "ISO certified processing. Third-party lab testing on every shipment." },
              { Icon: Award, t: "Certifications", d: "HALAL, ISO 9001, ISO 22000, and organic options available." },
              { Icon: Users, t: "Fair Trade", d: "Direct farmer partnerships ensuring fair compensation and sustainable practices." },
            ].map(({ Icon, t, d }, i) => (
              <div key={i} className="bg-[#0A0909] p-8">
                <Icon size={24} className="text-[#3FD97A]" />
                <h3 className="mt-6 font-serif text-2xl">{t}</h3>
                <p className="mt-3 text-sm text-foreground/70 leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="mt-28">
          <div className="overline">Our Journey</div>
          <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight mb-14">
            A decade of growth
          </h2>
          
          <div className="space-y-8">
            {TIMELINE.map((item, i) => (
              <div key={i} className="border-l-2 border-[#3FD97A] pl-8 py-4">
                <div className="flex items-baseline gap-4">
                  <span className="font-serif text-4xl text-[#3FD97A]">{item.year}</span>
                  <span className="font-serif text-2xl">{item.event}</span>
                </div>
                <p className="mt-2 text-foreground/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Facts */}
        <div className="mt-28 border border-border">
          <div className="grid grid-cols-2 md:grid-cols-4">
            {FACTS.map((fact, i) => (
              <div 
                key={i} 
                className={`p-8 md:p-10 ${i !== 0 ? "border-l border-border" : ""} ${i % 2 !== 0 ? "border-l border-border" : ""}`}
              >
                <div className="overline">{fact.label}</div>
                <div className="font-serif text-5xl mt-3 text-[#3FD97A]">{fact.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Facility */}
        <div className="mt-28 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="aspect-[4/3] bg-[#141212] overflow-hidden order-2 lg:order-1">
            <img 
              src="https://images.pexels.com/photos/4483610/pexels-photo-4483610.jpeg?auto=compress&cs=tinysrgb&w=1200&h=900&fit=crop" 
              alt="Processing facility" 
              className="w-full h-full object-cover" 
            />
          </div>
          <div className="order-1 lg:order-2">
            <div className="overline">Infrastructure</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight">
              State-of-the-art processing
            </h2>
            <p className="mt-6 text-foreground/75 leading-relaxed">
              Our ISO-certified facility near Lahore features modern cleaning, sorting, grading, 
              and packaging lines. Climate-controlled storage maintains product integrity from 
              receipt to shipment.
            </p>
            <p className="mt-4 text-foreground/75 leading-relaxed">
              Advanced color sorters, metal detectors, and lab equipment ensure every shipment 
              meets international food safety standards.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
